import { Link } from '@solidjs/router';
import { createSignal, JSX, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getImageServiceSrcPath } from '../image-component/image-component';
import { ArrowRightIcon } from '../icons-library/arrow-right';
import removeAmpersand from '../../tools/remove-ampersand';
import { hitHref, hitOpensInNewTab, SearchHit } from './search-shared';
import { SearchDocumentSymbol } from './search-document-symbol';

/* 2x the rendered 240px thumbnail. Both have to be in the image service's ALLOWED_SIZES. */
const THUMB_SRC_WIDTH = 480;

type SearchResultRowProps = {
    hit: SearchHit;
};

/* Document types the file library writes in acronym form. */
const UPPERCASE_WORDS = new Set(['ifu', 'mri', 'ec', 'pdf', 'faq']);

/* Document type names come out of the file library as lowercase slugs ("ifu",
   "technical-information"); the row shows them as words. */
export const formatDocumentType = (raw: string) =>
    raw
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((word) => (UPPERCASE_WORDS.has(word.toLowerCase())
            ? word.toUpperCase()
            : word.charAt(0).toUpperCase() + word.slice(1)))
        .join(' ');

/* One row of the results list (Figma 1602:11181 desktop, 1436:10395 mobile): thumbnail,
   content-type label, title, excerpt and a tertiary arrow in the bottom-right corner. Below
   the tablet breakpoint the thumbnail goes and the arrow drops under the text. The thumbnail
   is drawn a little smaller than the design asks (240x160 for 300x200), per the ticket. With
   an image it has no background (many product shots carry their own white backdrop, which
   clashes with the wash), and products sit whole and inset on white; without one it falls
   back to the brand wash. Documents never have an image and get a document symbol in the middle of the wash instead. The whole row is one link. */
export const SearchResultRow = (props: SearchResultRowProps) => {
    const { localize, imagesServiceUrl, supportedImageFormats } = useContext(AppContext);

    const typeLabel = () => {
        switch (props.hit.type) {
            case 'page':
                return localize('page', 'Page');
            case 'product':
                return localize('product', 'Product');
            case 'blog-and-news':
                return localize('blog', 'Blogs and news');
            case 'event':
                return localize('event', 'Event');
            case 'video-item':
                return localize('video', 'Video');
            case 'document':
                /* The document type from the file library (e.g. "IFU") is the useful label. */
                return props.hit.tags?.[0] ? formatDocumentType(props.hit.tags[0]) : localize('document', 'Document');
            default:
                return localize(props.hit.type, props.hit.type);
        }
    };

    /* Product shots are cut-outs: show the whole product, inset on white, instead of cropping. */
    const isProduct = () => props.hit.type === 'product';

    /* A broken image falls back to the wash like a missing one. */
    const [imageFailed, setImageFailed] = createSignal(false);

    const imageSrc = () => {
        if (!props.hit.image?.url || imageFailed()) {
            return '';
        }

        return getImageServiceSrcPath({
            imagesServiceUrl,
            supportedImageFormats,
            src: props.hit.image.url,
            width: THUMB_SRC_WIDTH,
            height: 'AUTO',
        });
    };

    const content = (): JSX.Element => (
        <div class="flex items-stretch">
            <div
                class="relative hidden h-cvi-search-thumb-h w-cvi-search-thumb-w shrink-0 items-center justify-center overflow-hidden rounded-cvi-lg tablet:flex"
                classList={{
                    'bg-cvi-bg-brand-card': !imageSrc(),
                    'bg-cvi-bg-default': !!imageSrc() && isProduct(),
                }}
            >
                <Show
                    when={imageSrc()}
                    fallback={
                        <Show when={props.hit.type === 'document'}>
                            <SearchDocumentSymbol documentType={props.hit.tags?.[0]} class="size-cvi-search-thumb-symbol" />
                        </Show>
                    }
                >
                    <img
                        src={imageSrc()}
                        alt={props.hit.image?.alt || ''}
                        loading="lazy"
                        onError={() => setImageFailed(true)}
                        class={clsx('absolute inset-0 size-full', isProduct() ? 'object-contain p-cvi-5' : 'object-cover')}
                    />
                </Show>
            </div>

            <div class="relative flex min-w-0 flex-1 flex-col tablet:p-cvi-8">
                <p class="m-0 body-md font-cvi-regular text-cvi-fg-default">{typeLabel()}</p>

                <h3 class="mx-0 my-cvi-5 max-w-cvi-search-text heading-3xl font-cvi-regular text-cvi-fg-default line-clamp-3 transition-colors duration-200 group-hover:text-cvi-fg-link">
                    {removeAmpersand(props.hit.postTitle)}
                </h3>

                <Show when={props.hit.description}>
                    <p class="m-0 max-w-cvi-search-text body-sm font-cvi-regular text-cvi-fg-muted line-clamp-2">
                        {props.hit.description}
                    </p>
                </Show>

                {/* Decorative: the row itself is the link. In flow under the text on small
                    screens, pinned to the row's bottom-right corner from tablet up. */}
                <span
                    aria-hidden="true"
                    class="icon-button-tertiary icon-button-tertiary-sm mt-cvi-6 self-end tablet:absolute tablet:bottom-0 tablet:right-cvi-8 tablet:mt-0"
                >
                    <ArrowRightIcon size="small" ariaHidden />
                </span>
            </div>
        </div>
    );

    const linkClasses = 'group block text-cvi-fg-default no-underline';

    return (
        <Show
            when={hitOpensInNewTab(props.hit)}
            fallback={
                <Link href={hitHref(props.hit)} class={linkClasses}>
                    {content()}
                </Link>
            }
        >
            <a href={hitHref(props.hit)} target="_blank" rel="noopener noreferrer" class={clsx(linkClasses)}>
                {content()}
            </a>
        </Show>
    );
};
